import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { CURATED_DEEPLINK_PRESETS } from '../constants/deeplinkPresets'
import type {
  DeeplinkCategory,
  DeeplinkPreset,
} from '../interfaces/deeplinkCatalog'
import { isValidDeeplink, openDeeplink } from '../utils/deeplink'
import { filterDeeplinkPresets } from '../utils/deeplinkCatalogFilter'
import { useDeeplinkCatalog } from '../utils/useDeeplinkCatalog'

export interface InitialCatalogFormState {
  isOpen: boolean
  scheme_url: string
  name?: string
  category?: string
  icon?: string
}

export interface DeeplinkCatalogSheetProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (schemeUrl: string) => void
  currentUrl?: string
  initialForm?: InitialCatalogFormState | null
}

type CatalogTab = 'curated' | 'custom' | 'community'

const PRESET_CATEGORIES: DeeplinkCategory[] = [
  'All',
  'Productivity & Notes',
  'Audio & Media',
  'Reading',
  'Learning',
  'Fitness & Health',
  'General',
]

export function DeeplinkCatalogSheet({
  isOpen,
  onClose,
  onSelect,
  currentUrl,
  initialForm,
}: DeeplinkCatalogSheetProps): ReactElement | null {
  const {
    customPresets,
    communityPresets,
    loading,
    addCustomPreset,
    editCustomPreset,
    removeCustomPreset,
  } = useDeeplinkCatalog()

  const [activeTab, setActiveTab] = useState<CatalogTab>('curated')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedCategory, setSelectedCategory] =
    useState<DeeplinkCategory>('All')

  // Form state for creating / editing custom preset
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formName, setFormName] = useState<string>('')
  const [formScheme, setFormScheme] = useState<string>('')
  const [formCategory, setFormCategory] = useState<string>(
    'Productivity & Notes',
  )
  const [formIcon, setFormIcon] = useState<string>('📱')
  const [formIsPublic, setFormIsPublic] = useState<boolean>(false)
  const [formError, setFormError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const activePresets = useMemo(() => {
    switch (activeTab) {
      case 'curated':
        return CURATED_DEEPLINK_PRESETS
      case 'custom':
        return customPresets
      case 'community':
        return communityPresets
    }
  }, [activeTab, customPresets, communityPresets])

  const filteredPresets = useMemo(() => {
    return filterDeeplinkPresets(activePresets, searchQuery, selectedCategory)
  }, [activePresets, searchQuery, selectedCategory])

  useEffect(() => {
    if (isOpen) {
      if (initialForm?.isOpen) {
        setActiveTab('custom')
        setIsFormOpen(true)
        setEditingId(null)
        setFormScheme(initialForm.scheme_url)
        setFormName(initialForm.name ?? '')
        setFormCategory(initialForm.category ?? 'Productivity & Notes')
        setFormIcon(initialForm.icon ?? '📱')
        setFormIsPublic(false)
        setFormError('')
      } else {
        setActiveTab('curated')
        setIsFormOpen(false)
        setEditingId(null)
        setFormError('')
      }
    }
  }, [isOpen, initialForm])

  if (!isOpen) return null

  function resetForm(): void {
    setEditingId(null)
    setFormName('')
    setFormScheme('')
    setFormCategory('Productivity & Notes')
    setFormIcon('📱')
    setFormIsPublic(false)
    setFormError('')
    setIsFormOpen(false)
  }

  function handleStartAdd(): void {
    setEditingId(null)
    setFormName('')
    setFormScheme('')
    setFormCategory('Productivity & Notes')
    setFormIcon('📱')
    setFormIsPublic(false)
    setFormError('')
    setIsFormOpen(true)
  }

  function handleStartEdit(preset: DeeplinkPreset): void {
    setEditingId(preset.id)
    setFormName(preset.name)
    setFormScheme(preset.scheme_url)
    setFormCategory(preset.category)
    setFormIcon(preset.icon)
    setFormIsPublic(Boolean(preset.is_public))
    setFormError('')
    setIsFormOpen(true)
  }

  async function handleSavePreset(e: FormEvent): Promise<void> {
    e.preventDefault()
    setFormError('')

    const name = formName.trim()
    const scheme = formScheme.trim()

    if (!name) {
      setFormError('App or preset name is required.')
      return
    }

    if (!scheme || !isValidDeeplink(scheme)) {
      setFormError(
        'Please enter a valid URL scheme (e.g. myapp://, shortcuts://..., https://...)',
      )
      return
    }

    setIsSubmitting(true)
    try {
      if (editingId) {
        const existing = customPresets.find((p) => p.id === editingId)
        if (existing) {
          await editCustomPreset({
            ...existing,
            name,
            scheme_url: scheme,
            category: formCategory,
            icon: formIcon.trim() || '📱',
            is_public: formIsPublic,
          })
        }
      } else {
        const created = await addCustomPreset({
          name,
          scheme_url: scheme,
          category: formCategory,
          icon: formIcon.trim() || '📱',
          is_public: formIsPublic,
        })
        if (initialForm?.isOpen) {
          handleSelectPreset(created.scheme_url)
          return
        }
      }
      resetForm()
    } catch (err) {
      console.error('Failed to save preset:', err)
      setFormError('Failed to save deeplink preset.')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (window.confirm('Are you sure you want to delete this custom preset?')) {
      await removeCustomPreset(id)
      if (editingId === id) {
        resetForm()
      }
    }
  }

  function handleSelectPreset(schemeUrl: string): void {
    onSelect(schemeUrl)
    onClose()
  }

  return (
    <>
      {/* High z-index backdrop to overlay HabitFormSheet smoothly */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-xs transition-opacity"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={-1}
        aria-label="Close catalog"
      />

      <div className="fixed inset-0 z-[70] m-auto flex h-fit max-h-[88dvh] w-[calc(100%-1.5rem)] max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-gray-100 border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">📱</span>
            <h2 className="font-bold text-gray-900 text-lg">
              Deeplink Catalog
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs: Curated | My Presets | Community */}
        <div className="flex border-gray-100 border-b bg-gray-50/70 p-1.5 text-sm">
          <button
            type="button"
            onClick={() => {
              setActiveTab('curated')
              setIsFormOpen(false)
            }}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 font-medium transition-all ${
              activeTab === 'curated'
                ? 'bg-white text-indigo-600 shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <SparklesIcon className="h-4 w-4" />
            <span>Presets</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('custom')
              setIsFormOpen(false)
            }}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 font-medium transition-all ${
              activeTab === 'custom'
                ? 'bg-white text-indigo-600 shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <UserIcon className="h-4 w-4" />
            <span>My Presets ({customPresets.length})</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('community')
              setIsFormOpen(false)
            }}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 font-medium transition-all ${
              activeTab === 'community'
                ? 'bg-white text-indigo-600 shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <GlobeAltIcon className="h-4 w-4" />
            <span>Community</span>
          </button>
        </div>

        {/* Search & Category Filter Section */}
        <div className="flex flex-col gap-2.5 border-gray-100 border-b px-5 py-3">
          <div className="relative flex items-center">
            <MagnifyingGlassIcon className="absolute left-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by app, scheme, or category..."
              className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-2 pr-9 pl-9 text-gray-800 text-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Category Filter Pills */}
          <div className="no-scrollbar flex gap-1.5 overflow-x-auto pb-1">
            {PRESET_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`shrink-0 rounded-lg px-2.5 py-1 font-medium text-xs transition-colors ${
                  selectedCategory === cat
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Action bar for My Presets (Add button) */}
        {activeTab === 'custom' && !isFormOpen && (
          <div className="flex items-center justify-between border-gray-100 border-b bg-indigo-50/30 px-5 py-2.5">
            <span className="text-gray-600 text-xs">
              Saved custom apps & Apple Shortcuts
            </span>
            <button
              type="button"
              onClick={handleStartAdd}
              className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 font-medium text-white text-xs hover:bg-indigo-700"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add custom deeplink
            </button>
          </div>
        )}

        {/* Body content scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Add / Edit Form */}
          {isFormOpen && (
            <form
              onSubmit={handleSavePreset}
              className="mb-4 flex flex-col gap-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">
                  {editingId ? 'Edit Custom Deeplink' : 'New Custom Deeplink'}
                </h3>
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1">
                  <label
                    htmlFor="preset-icon"
                    className="mb-1 block font-medium text-gray-700 text-xs"
                  >
                    Icon
                  </label>
                  <input
                    id="preset-icon"
                    type="text"
                    maxLength={12}
                    value={formIcon}
                    onChange={(e) => setFormIcon(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white p-2 text-center text-base focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div className="col-span-3">
                  <label
                    htmlFor="preset-name"
                    className="mb-1 block font-medium text-gray-700 text-xs"
                  >
                    App or Workflow Name
                  </label>
                  <input
                    id="preset-name"
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. My Workout Shortcut"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="preset-scheme"
                  className="mb-1 block font-medium text-gray-700 text-xs"
                >
                  Deeplink URL Scheme
                </label>
                <input
                  id="preset-scheme"
                  type="text"
                  value={formScheme}
                  onChange={(e) => setFormScheme(e.target.value)}
                  placeholder="e.g. shortcuts://run-shortcut?name=Workout"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label
                    htmlFor="preset-category"
                    className="mb-1 block font-medium text-gray-700 text-xs"
                  >
                    Category
                  </label>
                  <select
                    id="preset-category"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  >
                    {PRESET_CATEGORIES.filter((c) => c !== 'All').map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center pt-5">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formIsPublic}
                      onChange={(e) => setFormIsPublic(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="font-medium text-gray-700 text-xs">
                      Share to Community
                    </span>
                  </label>
                </div>
              </div>

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-600 text-xs hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-lg bg-indigo-600 px-3.5 py-1.5 font-medium text-white text-xs hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving…' : 'Save Deeplink'}
                </button>
              </div>
            </form>
          )}

          {/* Preset list */}
          {loading &&
          activeTab === 'community' &&
          communityPresets.length === 0 ? (
            <div className="flex flex-col gap-2.5 py-4">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className="h-16 animate-pulse rounded-xl bg-gray-100"
                />
              ))}
            </div>
          ) : filteredPresets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <span className="text-3xl text-gray-300">🔍</span>
              <p className="mt-2 font-medium text-gray-600 text-sm">
                No deeplinks found
              </p>
              <p className="mt-0.5 text-gray-400 text-xs">
                {activeTab === 'custom'
                  ? 'Tap "+ Add custom deeplink" to create one!'
                  : 'Try adjusting your search query or category filter.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {filteredPresets.map((preset) => {
                const isCurrent =
                  Boolean(currentUrl) &&
                  currentUrl
                    ?.trim()
                    .toLowerCase()
                    .replace(/(?<!:)\/+$/, '') ===
                    preset.scheme_url
                      .trim()
                      .toLowerCase()
                      .replace(/(?<!:)\/+$/, '')
                const isCustom = activeTab === 'custom'

                return (
                  <div
                    key={preset.id}
                    className={`flex items-center justify-between rounded-xl border p-3 transition-all ${
                      isCurrent
                        ? 'border-indigo-500 bg-indigo-50/30'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-xl">
                        {preset.icon || '📱'}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="truncate font-semibold text-gray-900 text-sm">
                            {preset.name}
                          </h4>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-[10px] text-gray-500">
                            {preset.category}
                          </span>
                          {preset.is_public && (
                            <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 font-medium text-[10px] text-emerald-600">
                              Public
                            </span>
                          )}
                        </div>
                        <p className="truncate font-mono text-gray-400 text-xs">
                          {preset.scheme_url}
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5 pl-2">
                      {/* Test link button */}
                      <button
                        type="button"
                        onClick={() => openDeeplink(preset.scheme_url)}
                        title={`Test launch ${preset.name}`}
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                        aria-label={`Test ${preset.name}`}
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </button>

                      {/* Edit / Delete actions for custom presets */}
                      {isCustom && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(preset)}
                            title="Edit preset"
                            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                            aria-label="Edit preset"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(preset.id)}
                            title="Delete preset"
                            className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                            aria-label="Delete preset"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}

                      {/* Select button */}
                      <button
                        type="button"
                        onClick={() => handleSelectPreset(preset.scheme_url)}
                        className={`flex items-center gap-1 rounded-lg px-3 py-1.5 font-medium text-xs transition-colors ${
                          isCurrent
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800'
                        }`}
                      >
                        {isCurrent ? (
                          <>
                            <CheckIcon className="h-3.5 w-3.5" />
                            Selected
                          </>
                        ) : (
                          'Select'
                        )}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="flex items-center justify-between border-gray-100 border-t bg-gray-50 px-5 py-2.5 text-gray-500 text-xs">
          <span>{filteredPresets.length} deeplinks available</span>
          <button
            type="button"
            onClick={onClose}
            className="font-medium text-gray-600 hover:text-gray-900"
          >
            Close
          </button>
        </div>
      </div>
    </>
  )
}
